(() => {
  if (customElements.get('product-family')) return;

  class ProductFamily extends HTMLElement {
    static normalizeOptions(options, axisCount) {
      if (!Array.isArray(options) || options.length !== axisCount) return null;
      if (options.some(value => typeof value !== 'string' || !value.trim())) return null;
      return options.map(value => value.trim());
    }

    static validateFamily(payload) {
      const nonblank = value => typeof value === 'string' && value.trim().length > 0;
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
      if (payload.familyType !== 'product_family' || !nonblank(payload.familyId)) return null;
      if (payload.currentFamilyId !== payload.familyId || !nonblank(payload.name)) return null;
      if (!nonblank(payload.currentProductId)) return null;
      if (!Array.isArray(payload.axisNames) || payload.axisNames.length !== 3) return null;
      if (payload.axisNames.some(name => typeof name !== 'string')) return null;

      const names = payload.axisNames.map(name => name.trim());
      const firstInactive = names.indexOf('');
      const axisCount = firstInactive === -1 ? names.length : firstInactive;
      if (!axisCount || names.slice(axisCount).some(Boolean)) return null;
      const currentOptions = this.normalizeOptions(payload.currentOptions, axisCount);
      if (!currentOptions) return null;
      // Match the MVP's 50-product contract and Shopify Liquid's per-loop boundary.
      if (!Array.isArray(payload.members) || payload.members.length < 2 || payload.members.length > 50) return null;
      if (!Number.isInteger(payload.memberCount) || payload.memberCount !== payload.members.length) return null;

      const members = [];
      const memberIds = new Set();
      const membersByTuple = new Map();
      for (const member of payload.members) {
        if (!member || typeof member !== 'object' || Array.isArray(member)) return null;
        if (!nonblank(member.id) || memberIds.has(member.id)) return null;
        const options = this.normalizeOptions(member.options, axisCount);
        if (!options) return null;
        const tuple = JSON.stringify(options);
        if (membersByTuple.has(tuple)) return null;

        // Liquid supplies canonical, locale-aware product paths without queries.
        if (typeof member.url !== 'string' || !member.url.startsWith('/') || member.url.startsWith('//') || member.url.includes('\\')) return null;
        let url;
        try {
          url = new URL(member.url, window.location.origin);
        } catch {
          return null;
        }
        const segments = url.pathname.split('/').filter(Boolean);
        if (url.origin !== window.location.origin || url.search || url.hash || segments.length < 2 || segments[segments.length - 2] !== 'products') return null;

        const validatedMember = { id: member.id, url: url.pathname, options };
        memberIds.add(member.id);
        members.push(validatedMember);
        membersByTuple.set(tuple, validatedMember);
      }

      const current = members.find(member => member.id === payload.currentProductId);
      if (!current || JSON.stringify(current.options) !== JSON.stringify(currentOptions)) return null;
      return { name: payload.name.trim(), axisNames: names.slice(0, axisCount), members, membersByTuple, current };
    }

    static resolveChoice(family, axisIndex, value) {
      if (!Number.isInteger(axisIndex) || axisIndex < 0 || axisIndex >= family.axisNames.length) return null;
      const tuple = [...family.current.options];
      tuple[axisIndex] = value;
      return family.membersByTuple.get(JSON.stringify(tuple)) || null;
    }

    connectedCallback() {
      this.hidden = true;
      try {
        const family = ProductFamily.validateFamily(JSON.parse(this.getAttribute('data-product-family-payload')));
        if (!family) return;
        const heading = this.querySelector('[data-product-family-name]');
        const axes = Array.from(this.querySelectorAll('[data-product-family-axis]'));
        if (!heading || axes.length !== family.axisNames.length) return;

        // Prepare every axis before exposing any controls, including on reconnect.
        const preparedAxes = axes.map((axis, axisIndex) => {
          if (axis.closest('product-family') !== this || axis.getAttribute('data-product-family-axis') !== String(axisIndex)) throw new Error('Invalid family axis');
          const label = axis.querySelector('[data-product-family-axis-name]');
          const selected = axis.querySelector('[data-product-family-selected]');
          const choices = axis.querySelector('[data-product-family-choices]');
          if (!label || !selected || !choices) throw new Error('Incomplete family axis');
          const fragment = document.createDocumentFragment();
          const values = new Set(family.members.map(member => member.options[axisIndex]));

          for (const value of values) {
            const target = ProductFamily.resolveChoice(family, axisIndex, value);
            let choice;
            if (!target) {
              choice = document.createElement('button');
              choice.type = 'button';
              choice.disabled = true;
              choice.setAttribute('aria-disabled', 'true');
              choice.className = 'product-family__choice product-family__choice--unavailable';
            } else if (target.id === family.current.id) {
              choice = document.createElement('span');
              choice.setAttribute('aria-current', 'true');
              choice.className = 'product-family__choice product-family__choice--current';
            } else {
              choice = document.createElement('a');
              choice.href = target.url;
              choice.className = 'product-family__choice';
            }
            choice.textContent = value;
            fragment.appendChild(choice);
          }
          return { label, selected, choices, fragment };
        });

        heading.textContent = family.name;
        preparedAxes.forEach((axis, axisIndex) => {
          axis.label.textContent = family.axisNames[axisIndex];
          axis.selected.textContent = family.current.options[axisIndex];
          axis.choices.replaceChildren(axis.fragment);
        });
        this.hidden = false;
      } catch {
        // Invalid or incomplete payloads must leave the entire block hidden.
        this.hidden = true;
      }
    }
  }

  customElements.define('product-family', ProductFamily);
})();
